class EventList extends HTMLElement {
  constructor() {
    super();
  }

  pageCount = 16;
  events = [];
  initialized = false;
  pageInfo = null;

  connectedCallback() {
    this.init();
    this.lang = window.Shopify.locale || "en";
  }

  init() {
    this.categorySelect = this.querySelector('select[name="category"]');
    this.languageSelect = this.querySelector('select[name="language"]');
    this.regionSelect = this.querySelector('select[name="region"]');
    this.searchInput = this.querySelector('input[name="search"]');
    this.eventsContainer = this.querySelector(".event-list__events");
    this.noEventsContainer = this.querySelector(".event-list__no-events");
    this.loadingIndicator = this.querySelector(".event-list__loading");
    this.errorContainer = this.querySelector(".event-list__error");
    this.loadMoreContainer = this.querySelector(".event-list__load-more");
    this.loadMoreButton = this.loadMoreContainer.querySelector("button");
    this.loadMoreButton.addEventListener("click", () => this.onLoadMore());

    this.locationIcon = this.querySelector("#location-icon");
    this.placeholderImage = this.querySelector("#placeholder-image");
    this.dateIcon = this.querySelector("#date-icon");

    this.categorySelect?.addEventListener(
      "change",
      this.debounce(() => this.handleFilterChange(), 50),
    );
    this.languageSelect?.addEventListener(
      "change",
      this.debounce(() => this.handleFilterChange(), 50),
    );
    this.regionSelect?.addEventListener(
      "change",
      this.debounce(() => this.handleFilterChange(), 50),
    );
    this.searchInput?.addEventListener(
      "input",
      this.debounce(() => this.handleFilterChange(), 300),
    );

    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.has("after")) {
      urlSearchParams.delete("after");
      window.history.pushState(
        {},
        "",
        `${window.location.pathname}?${urlSearchParams.toString()}`,
      );
    }

    this.load();
  }

  handleFilterChange() {
    this.events = [];
    this.eventsContainer.innerHTML = "";
    this.loadMoreContainer.style.display = "none";
    this.pageInfo = null;

    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.has("after")) {
      urlSearchParams.delete("after");
      window.history.pushState(
        {},
        "",
        `${window.location.pathname}?${urlSearchParams.toString()}`,
      );
    }

    this.load();
  }

  onLoadMore() {
    if (this.pageInfo?.hasNextPage) {
      this.loadMoreContainer.style.display = "none";
      this.load(this.pageInfo.endCursor);
    } else {
      this.loadMoreContainer.style.display = "none";
    }
  }

  debounce(func, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  load(after) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    this.noEventsContainer.style.display = "none";

    const filter = {
      locale: window.Shopify.locale || "en",
      language:
        (this.initialized
          ? this.languageSelect?.value
          : urlSearchParams.get("language")) || undefined,
      search:
        (this.initialized
          ? this.searchInput?.value
          : urlSearchParams.get("search")) || undefined,
      region:
        (this.initialized
          ? this.regionSelect?.value
          : urlSearchParams.get("region")) || undefined,
      category:
        (this.initialized
          ? this.categorySelect?.value
          : urlSearchParams.get("category")) || undefined,
    };

    const url = new URL(window.location);
    Object.keys(filter).forEach((key) => {
      if (["locale"].includes(key)) {
        return;
      }

      if (filter[key]) {
        urlSearchParams.set(key, filter[key]);
      } else {
        urlSearchParams.delete(key);
      }
    });

    if (after) {
      urlSearchParams.set("after", after);
    }

    if (urlSearchParams.toString() !== window.location.search) {
      const newUrl = `${url.pathname}?${urlSearchParams.toString()}`;
      window.history.pushState({}, "", newUrl);
    }

    this.loadingIndicator.style.display = "block";

    const loadPromise = new Promise((resolve, reject) => {
      this.fetchResults(filter, after)
        .then((res) => {
          if (!res.ok) throw new Error("API Error");
          return res.json();
        })
        .then((data) => {
          const { events, eventRegions, eventCategories, eventLanguages } =
            data.data;
          const newEvents = events.edges.map((e) => e.node);
          const handles = newEvents.map((e) => e.handle).join(",");
          this.pageInfo = events.pageInfo;

          this.fetchMetaobjects(handles).then((metaobjectsData) => {
            const metaobjects = metaobjectsData?.data || [];
            const newEventsWithMetaobjects = newEvents.map((e) => {
              return {
                ...e,
                ...(metaobjects.find((m) => m.handle === e.handle) || {}),
              };
            });

            if (!this.initialized) {
              if (this.categorySelect) {
                this.categorySelect.insertAdjacentHTML(
                  "beforeend",
                  eventCategories
                    .map((c) => `<option value="${c}">${c}</option>`)
                    .join(""),
                );
                this.categorySelect.disabled = false;
                this.categorySelect.value = filter.category || "";
              }

              if (this.languageSelect) {
                this.languageSelect.insertAdjacentHTML(
                  "beforeend",
                  eventLanguages
                    .map((c) => `<option value="${c}">${c}</option>`)
                    .join(""),
                );
                this.languageSelect.disabled = false;
                this.languageSelect.value = filter.language || "";
              }

              if (this.regionSelect) {
                this.regionSelect.insertAdjacentHTML(
                  "beforeend",
                  eventRegions
                    .map((r) => `<option value="${r}">${r}</option>`)
                    .join(""),
                );
                this.regionSelect.disabled = false;
                this.regionSelect.value = filter.region || "";
              }

              if (this.searchInput) {
                this.searchInput.disabled = false;
                this.searchInput.value = filter.search || "";
              }
            }
            this.events = [...this.events, ...newEventsWithMetaobjects];
            this.renderEvents(newEventsWithMetaobjects);
            this.loadMoreContainer.style.display = this.pageInfo?.hasNextPage
              ? "flex"
              : "none";
            this.initialized = true;
            resolve();
          });
        })
        .catch((err) => {
          reject(err);
        });
    });

    loadPromise
      .catch((err) => {
        console.error(err);
        this.errorContainer.style.display = "block";
        this.loadMoreContainer.style.display = "none";
        this.eventsContainer.style.display = "none";
        this.noEventsContainer.style.display = "none";
      })
      .finally(() => {
        this.loadingIndicator.style.display = "none";
        if (this.events.length > 0) {
          this.noEventsContainer.style.display = "none";
          this.eventsContainer.style.display = "grid";
        } else {
          this.noEventsContainer.style.display = "flex";
          this.eventsContainer.style.display = "none";
        }
      });
  }

  renderEvents(newEvents) {
    const eventsHTML = newEvents.map((event) => {
      let eventDates = event.dates || [];
      if (eventDates.length > 0) {
        eventDates = eventDates.map((date) => {
          return date.split("T")[0];
        });
        eventDates = eventDates.sort((a, b) => new Date(a) - new Date(b));
        if (eventDates.length > 2) {
          eventDates = [eventDates[0], eventDates[eventDates.length - 1]];
        }
        eventDates = new Set(eventDates);
        eventDates = Array.from(eventDates);
      }

      const pathname = window.location.pathname.split("/");
      pathname.pop();
      pathname.push("event");
      pathname.push(event.handle);

      const title = event.data[this.lang].title || event.title;
      const category = event.data[this.lang].category || event.category;
      const city = event.data[this.lang].city || event.city;
      

      return `
        <a aria-label="${title}" href="${pathname.join("/")}" class="event-list__event-card">
        <div class="event-list__event-card-image">
          ${
            event.image?.url
              ? `
                <img
                  width="254"
                  height="auto"
                  src="${event.image.url}"
                  alt="${event.image.alt}"
                >
                `
              : this.placeholderImage.innerHTML
          }
          </div>
          <div class="event-list__event-card-content">
            <h4 class="event-list__event-card-subtitle">${category}</h4>
            <h3 class="event-list__event-card-title">${title}</h3>
            ${
              eventDates.length > 0
                ? `
                  <p class="event-list__event-card-detail">
                    ${this.dateIcon.innerHTML}
                    ${new Date(eventDates[0]).toLocaleDateString(window.Shopify.locale, { month: "long", day: "numeric" })}
                    ${eventDates.length > 1 ? `- ${new Date(eventDates[eventDates.length - 1]).toLocaleDateString(window.Shopify.locale, { month: "long", day: "numeric" })}` : ""}
                  </p>
                `
                : ""
            }
            <p class="event-list__event-card-detail">
              ${this.locationIcon.innerHTML}
              ${city}
            </p>
          </div>
        </a>
      `;
    });

    this.eventsContainer.insertAdjacentHTML("beforeend", eventsHTML.join(""));
  }

  fetchResults(filter, after) {
    return fetch(`/a/portal/public/graphql`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: `
          query Events($filter: EventFilterInput, $first: Int, $after: String, $locale: String!) {
            events(first: $first, filter: $filter, after: $after) {
              edges {
                node {
                  id
                  shopifyId
                  handle
                  data
                }
              }
              totalCount
              pageInfo {
                endCursor
                hasNextPage
              }
            }
            eventRegions(locale: $locale)
            eventCategories(locale: $locale)
            eventLanguages(locale: $locale)
          }
        `,
        variables: {
          filter,
          first: this.pageCount,
          after,
          locale: window.Shopify.locale || "en",
        },
      }),
    });
  }

  async fetchMetaobjects(handles) {
    if (!handles) {
      return [];
    }

    try {
      const metaobjectsURL = `/a/portal/api/v1/metaobjects?metaobject_type=event&metaobject_handles=${encodeURIComponent(handles)}`;
      const response = await fetch(metaobjectsURL, {
        method: "GET",
        headers: {
          Accept: "application/liquid",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const scriptTag = doc.getElementById("metaobjects-data");

      if (scriptTag && scriptTag.textContent) {
        return JSON.parse(scriptTag.textContent);
      }

      throw new Error("Metaobjects data not found in response");
    } catch (error) {
      console.error("Error fetching metaobjects:", error);
      throw error;
    }
  }

  disconnectedCallback() {
    // Cleanup on component removal
  }
}

customElements.define("event-list", EventList);
